import { createSelector } from 'reselect'

import {
  filterBySearh,
  filterBySort,
  filterByTeam,
  filterIncompleteOtherReviews,
  isReadyForGenerateT1Report,
  isReadyForGenerateT2Report,
} from 'helpers/selectorHelpers'
import { STATUS_FILTER } from 'app/constants/reviews'

const getReviews = state => state.get('persist').reviews

export const getReviewsUsers = createSelector(getReviews, state => state.get('users'))

const getReviewsFilter = createSelector(getReviews, state => {
  return state.get('filter')
})

export const getReviewsFilterByTeam = createSelector(getReviewsFilter, state => state.get('byTeam'))

export const getReviewsFilterBySearch = createSelector(getReviewsFilter, state => state.get('bySearch'))

export const getReviewsFilterByStatus = createSelector(getReviewsFilter, state => state.get('byStatus'))

export const getReviewsFilterBySort = createSelector(getReviewsFilter, state => state.get('bySort'))

export const getReviewsSortBy = createSelector(getReviewsFilterBySort, bySort => bySort.get('by'))

export const getReviewsSortDesc = createSelector(getReviewsFilterBySort, bySort => bySort.get('desc'))

const getReviewsUsersFilteredByTeam = createSelector(getReviewsUsers, getReviewsFilterByTeam, (list, byTeam) => {
  return filterByTeam(list, byTeam)
})

export const getReviewsUsersIncompleteSelfReviewsT2SendInvitation = createSelector(
  getReviewsUsersFilteredByTeam,
  list => list.filter(user => !user.get('t2Self') && user.get('t1ReportCreated') && !user.get('isT2SelfInvitationSent'))
)

export const getReviewsUsersIncompleteSelfReviewsT2SendReminder = createSelector(getReviewsUsersFilteredByTeam, list =>
  list.filter(user => !user.get('t2Self') && user.get('t1ReportCreated') && user.get('isT2SelfInvitationSent'))
)

export const getReviewsUsersIncompleteOtherReviewsT1 = createSelector(getReviewsUsersFilteredByTeam, list =>
  list.filter(
    user =>
      !user.get('t1ReportCreated') &&
      (filterIncompleteOtherReviews(user.get('t1Employee')) ||
        filterIncompleteOtherReviews(user.get('t1Superior')) ||
        filterIncompleteOtherReviews(user.get('t1Peer')))
  )
)

export const getReviewsUsersIncompleteOtherReviewsT2 = createSelector(getReviewsUsersFilteredByTeam, list =>
  list.filter(user => {
    return (
      !user.get('t2ReportCreated') &&
      user.get('t1ReportCreated') &&
      user.get('t2Self') &&
      (filterIncompleteOtherReviews(user.get('t2Employee')) ||
        filterIncompleteOtherReviews(user.get('t2Superior')) ||
        filterIncompleteOtherReviews(user.get('t2Peer')))
    )
  })
)

export const getReviewsUsersT1Ready = createSelector(getReviewsUsersFilteredByTeam, list =>
  list.filter(user => isReadyForGenerateT1Report(user))
)

export const getReviewsUsersT2Ready = createSelector(getReviewsUsersFilteredByTeam, list =>
  list.filter(user => isReadyForGenerateT2Report(user))
)

const getReviewsUsersFilteredByStatus = createSelector(
  getReviewsUsersFilteredByTeam,
  getReviewsUsersIncompleteOtherReviewsT1,
  getReviewsUsersIncompleteOtherReviewsT2,
  getReviewsUsersIncompleteSelfReviewsT2SendInvitation,
  getReviewsUsersIncompleteSelfReviewsT2SendReminder,
  getReviewsUsersT1Ready,
  getReviewsUsersT2Ready,
  getReviewsFilterByStatus,
  (
    list,
    listIncompleteOtherReviewsT1,
    listIncompleteOtherReviewsT2,
    listIncompleteSelfReviewsT2SendInvitation,
    listIncompleteSelfReviewsT2SendReminder,
    listT1Ready,
    listT2Ready,
    byStatus
  ) => {
    return (
      {
        [STATUS_FILTER.INCOMPLETE_OTHER_REVIEWS_T1]: listIncompleteOtherReviewsT1,
        [STATUS_FILTER.INCOMPLETE_SELF_REVIEW_T2_SEND_INVITATION]: listIncompleteSelfReviewsT2SendInvitation,
        [STATUS_FILTER.INCOMPLETE_SELF_REVIEW_T2_SEND_REMINDER]: listIncompleteSelfReviewsT2SendReminder,
        [STATUS_FILTER.INCOMPLETE_OTHER_REVIEWS_T2]: listIncompleteOtherReviewsT2,
        [STATUS_FILTER.T1_READY]: listT1Ready,
        [STATUS_FILTER.T2_READY]: listT2Ready,
      }[byStatus] || list
    )
  }
)

const getReviewsUsersFilteredBySearch = createSelector(
  getReviewsUsersFilteredByStatus,
  getReviewsFilterBySearch,
  (list, bySearch) => {
    return filterBySearh(list, bySearch)
  }
)

const getReviewsUsersFilteredBySort = createSelector(
  getReviewsUsersFilteredBySearch,
  getReviewsFilterBySort,
  (list, bySort) => {
    const by = bySort.get('by')
    const desc = bySort.get('desc')
    return filterBySort(list, by, desc)
  }
)

const getReviewsUsersFilteredSize = createSelector(getReviewsUsersFilteredBySort, list => list.size)

export const getReviewsPagination = createSelector(getReviews, reviews => reviews.get('pagination'))

export const getReviewsPaginationCurrent = createSelector(getReviewsPagination, pagination => pagination.get('current'))

const getReviewsPaginationPerPage = createSelector(getReviewsPagination, pagination => pagination.get('perPage'))

export const getReviewsUsersFilteredTotalPages = createSelector(
  getReviewsUsersFilteredSize,
  getReviewsPaginationPerPage,
  (total, perPage) => Math.ceil(total / perPage)
)

export const getReviewsUsersFilteredBySortForCurrentPage = createSelector(
  getReviewsUsersFilteredBySort,
  getReviewsPaginationCurrent,
  getReviewsPaginationPerPage,
  (all, currentPage, perPage) => {
    return all.skip(currentPage * perPage).take(perPage)
  }
)

export const getReviewsMessagingInitialValues = currentTab =>
  createSelector(getReviews, state => state.getIn(['messaging', currentTab]))

const getHelper = createSelector(getReviews, reviews => reviews.get('helper'))

export const getHelperRequestElement = createSelector(getHelper, helper => helper.get('requestElement'))

const getReviewsReport = createSelector(getReviews, reviews => reviews.get('report'))

export const getReviewsReportData = createSelector(getReviewsReport, report => report.get('data'))

export const isReviewsReportPending = createSelector(getReviewsReport, report => report.get('pending'))
